import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, InfoIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface AuthFormProps {
  className?: string;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const AuthForm: React.FC<AuthFormProps> = ({ className = '' }) => {
  const { signIn, signUp, signInWithGoogle, signInWithMicrosoft, signInWithLinkedIn, resetPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'register' ? 'register' : 'login');
  const [formState, setFormState] = useState<FormState>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const navigate = useNavigate();

  // Update active tab when URL changes
  useEffect(() => {
    const tab = searchParams.get('tab');
    setActiveTab(tab === 'register' ? 'register' : 'login');
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState({
      ...formState,
      [name]: value,
    });
    // Clear error when user types
    setFormError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError('');
    
    try {
      const { data, error } = await signIn(formState.email, formState.password);
      
      if (error) {
        throw error;
      }

      if (data?.session) {
        toast.success('Successfully logged in!');
        // Clear form data
        setFormState({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
        });
        // Ensure we wait for the state updates before navigation
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 0);
      } else {
        throw new Error('Login failed - No session created');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setFormError(error.message || 'Failed to login. Please try again.');
      // Clear password field on error
      setFormState(prev => ({
        ...prev,
        password: ''
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError('');
    
    if (formState.password !== formState.confirmPassword) {
      setFormError('Passwords do not match');
      setIsLoading(false);
      return;
    }
    
    try {
      await signUp(formState.email, formState.password, {
        first_name: formState.firstName,
        last_name: formState.lastName
      });
      
      // Only show success if no error has been set during signup
      if (!formError) {
        toast.success('Account created! Please check your email to confirm your account.');
        
        // Clear form data after successful signup
        setFormState({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
        });
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      // Check for "already exists" in the error message
      if (error.message && error.message.includes('already exists')) {
        setFormError('An account with this email already exists. Please sign in instead.');
        // Automatically switch to login tab
        const loginTab = document.querySelector('[data-state="inactive"][data-value="login"]');
        if (loginTab) {
          (loginTab as HTMLElement).click();
        }
      } else {
        setFormError(error.message || 'An error occurred during signup');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthLogin = async (provider: 'google' | 'microsoft' | 'linkedin') => {
    setIsLoading(true);
    setFormError('');
    
    try {
      if (provider === 'google') {
        await signInWithGoogle();
      } else if (provider === 'microsoft') {
        await signInWithMicrosoft();
      } else if (provider === 'linkedin') {
        await signInWithLinkedIn();
      }
    } catch (error: any) {
      console.error(`Error signing in with ${provider}:`, error);
      setFormError(error.message || `Failed to sign in with ${provider}`);
      toast.error(`Failed to sign in with ${provider}`);
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError('');
    
    try {
      await resetPassword(formState.email);
      toast.success('Password reset email sent! Please check your inbox.');
      setShowForgotPassword(false);
    } catch (error: any) {
      setFormError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function for direct Hubspot API call as fallback
  const sendToHubspotDirectly = async (email: string, firstName: string, lastName: string, company: string = '') => {
    // No direct API call - function left for compatibility
    return null;
  };

  // If showing forgot password form
  if (showForgotPassword) {
    return (
      <Card className={`glass-card w-full max-w-md mx-auto ${className}`}>
        <CardHeader>
          <CardTitle>Reset Password</CardTitle>
          <CardDescription>
            Enter your email address and we'll send you a link to reset your password.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleForgotPassword}>
          <CardContent className="space-y-4">
            {formError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email</Label>
              <Input
                id="reset-email"
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formState.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <Button type="submit" className="w-full gradient-button" disabled={isLoading}>
              {isLoading ? "Sending..." : "Send Reset Link"}
            </Button>
          </CardContent>
        </form>
        <CardFooter className="flex justify-center">
          <Button variant="link" onClick={() => setShowForgotPassword(false)}>
            Back to Login
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className={`glass-card w-full max-w-md mx-auto ${className}`}>
      <Tabs value={activeTab} className="w-full" onValueChange={setActiveTab}>
        <CardHeader className="pb-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="register">Register</TabsTrigger>
          </TabsList>
        </CardHeader>
        
        <TabsContent value="login">
          <CardContent className="space-y-4">
            <Alert className="bg-indigo-50 border-indigo-200 text-indigo-800">
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                <span className="font-medium">Looking for <span className="text-indigo-700 font-bold underline">Mock Interview Practice</span>?</span> Click <a href="https://mock.roundone.ai/auth" className="font-semibold text-indigo-600 hover:text-indigo-800 underline">HERE</a> to access our dedicated practice environment.
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <h3 className="text-sm font-medium text-center mb-1">Sign in with a social account for faster access</h3>
              
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => handleOAuthLogin('google')}
                disabled={isLoading}
                className="w-full"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  className="mr-2"
                >
                  <path
                    fill="currentColor"
                    d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 1 1 0-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0 0 12.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"
                  />
                </svg>
                Continue with Google
              </Button>

              <Button 
                type="button"
                variant="outline"
                onClick={() => handleOAuthLogin('linkedin')}
                disabled={isLoading}
                className="w-full"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  className="mr-2"
                >
                  <path
                    fill="currentColor"
                    d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"
                  />
                </svg>
                Continue with LinkedIn
              </Button>

              <div className="flex flex-col">
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-card px-2 text-muted-foreground">Other options</span>
                  </div>
                </div>

                <details className="w-full text-center">
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-gray-700 mb-4">
                    Sign in with email instead
                  </summary>
                  
                  {formError && (
                    <Alert variant="destructive" className="py-2 mb-3">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleLogin} className="text-left">
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label htmlFor="email">Email</Label>
                        <Input 
                          id="email" 
                          type="email" 
                          name="email"
                          placeholder="Enter your email" 
                          value={formState.email}
                          onChange={handleInputChange}
                          required 
                        />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password">Password</Label>
                        </div>
                        <Input 
                          id="password" 
                          type="password" 
                          name="password"
                          placeholder="••••••••" 
                          value={formState.password}
                          onChange={handleInputChange}
                          required 
                        />
                      </div>
                      
                      <Button type="submit" className="w-full gradient-button" disabled={isLoading}>
                        {isLoading ? "Signing in..." : "Sign In"}
                      </Button>

                      <div className="text-center">
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="text-xs"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowForgotPassword(true);
                          }}
                        >
                          Forgot password?
                        </Button>
                      </div>
                    </div>
                  </form>
                </details>
              </div>
            </div>
          </CardContent>
        </TabsContent>
        
        <TabsContent value="register">
          <CardContent className="space-y-4">
            <Alert className="bg-indigo-50 border-indigo-200 text-indigo-800">
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                <span className="font-medium">Looking for <span className="text-indigo-700 font-bold underline">Mock Interview Practice</span>?</span> Click <a href="https://mock.roundone.ai/auth" className="font-semibold text-indigo-600 hover:text-indigo-800 underline">HERE</a> to access our dedicated practice environment.
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <h3 className="text-sm font-medium text-center mb-1">Create an account in seconds with a social account</h3>

              <Button 
                type="button" 
                variant="outline"
                onClick={() => handleOAuthLogin('google')}
                disabled={isLoading}
                className="w-full"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  className="mr-2"
                >
                  <path
                    fill="currentColor"
                    d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 1 1 0-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0 0 12.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"
                  />
                </svg>
                Continue with Google
              </Button>

              <Button 
                type="button"
                variant="outline"
                onClick={() => handleOAuthLogin('linkedin')}
                disabled={isLoading}
                className="w-full"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  className="mr-2"
                >
                  <path
                    fill="currentColor"
                    d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"
                  />
                </svg>
                Continue with LinkedIn
              </Button>
              
              <div className="flex flex-col">
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-card px-2 text-muted-foreground">Other options</span>
                  </div>
                </div>

                <details className="w-full text-center">
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-gray-700 mb-4">
                    Create account with email instead
                  </summary>
                  
                  {formError && (
                    <Alert variant="destructive" className="py-2 mb-3">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleSignup} className="text-left">
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="firstName">First Name</Label>
                          <Input 
                            id="firstName" 
                            name="firstName"
                            placeholder="John" 
                            value={formState.firstName}
                            onChange={handleInputChange}
                            required 
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="lastName">Last Name</Label>
                          <Input 
                            id="lastName" 
                            name="lastName"
                            placeholder="Doe" 
                            value={formState.lastName}
                            onChange={handleInputChange}
                            required 
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="register-email">Email</Label>
                        <Input 
                          id="register-email" 
                          type="email" 
                          name="email"
                          placeholder="john.doe@example.com" 
                          value={formState.email}
                          onChange={handleInputChange}
                          required 
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="register-password">Password</Label>
                        <Input 
                          id="register-password" 
                          type="password" 
                          name="password"
                          placeholder="••••••••" 
                          value={formState.password}
                          onChange={handleInputChange}
                          required 
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="confirmPassword">Confirm Password</Label>
                        <Input 
                          id="confirmPassword" 
                          type="password" 
                          name="confirmPassword"
                          placeholder="••••••••" 
                          value={formState.confirmPassword}
                          onChange={handleInputChange}
                          required 
                        />
                      </div>
                      <Button type="submit" className="w-full gradient-button" disabled={isLoading}>
                        {isLoading ? "Creating account..." : "Create Account"}
                      </Button>
                    </div>
                  </form>
                </details>
              </div>
            </div>
          </CardContent>
        </TabsContent>
        
        <CardFooter className="flex justify-center text-xs text-muted-foreground pt-2 pb-4">
          By signing in, you agree to our Terms of Service and Privacy Policy
        </CardFooter>
      </Tabs>
    </Card>
  );
};

export default AuthForm;
